<template>
    <div v-if="activeExportInvoice" class="pa-5 pt-1 compact-form">
        <div v-for="(item, index) in activeExportInvoice.rows" :key="'invoice_row_' + item.id">
            <v-row no-gutters class="align-baseline mb-3">
                <v-col class="mr-1" cols="1">
                    <v-text-field
                        :value="item.code"
                        :label="'Product code' | t"
                        class="text-field-item t-spacing"
                        dense
                        disabled
                        outlined
                        hide-details
                    />
                </v-col>
                <v-col cols="5" class="mr-1">
                    <v-combobox
                        v-model="autocomplete[index]"
                        :items="products"
                        :label="'Product' | t"
                        :loading="isLoading"
                        :value="autocomplete[index]"
                        class="pa-0 t-spacing"
                        color="bal-red"
                        hide-no-data
                        hide-details
                        hide-selected
                        item-text="custom_name"
                        item-value="name"
                        outlined
                        @input="updateComboboxValue(index)"
                        dense
                    />
                </v-col>

                <v-col class="mr-1" cols="1">
                    <v-text-field
                        v-model="localRowValues[index].sale_price"
                        :label="'Sale price' | t"
                        class="text-field-item mt-2 t-spacing"
                        dense
                        outlined
                        step="0.1"
                        hide-details
                        type="number"
                        @input="debouncedUpdateRow($event, 'sale_price', index)"
                        @keypress="onlyIntegers"
                    />
                </v-col>
                <v-col class="mr-1" cols="2">
                    <v-text-field
                        v-model="localRowValues[index].amount"
                        :label="'Amount' | t"
                        class="text-field-item t-spacing"
                        dense
                        outlined
                        hide-details
                        step="10"
                        type="number"
                        @input="debouncedUpdateRow($event, 'amount', index)"
                        @keypress="onlyIntegers"
                    />
                </v-col>
                <v-col class="mr-1" cols="1">
                    <v-text-field
                        v-model="localRowValues[index].own_price"
                        class="text-field-item t-spacing"
                        :label="'Export price' | t"
                        dense
                        outlined
                        hide-details
                        step="0.1"
                        type="number"
                        @input="debouncedUpdateRow($event, 'own_price', index)"
                        @keypress="onlyIntegers"
                    />
                </v-col>
                <v-col cols="1" style="text-align:right;">
                    <v-icon small @click="handleDuplicateClick(index)">fa fa-clone</v-icon>
                    <v-icon @click.stop="deleteRow(index)">delete</v-icon>
                </v-col>
            </v-row>

            <v-row class="mr-2" justify="end" no-gutters></v-row>
        </div>
        <v-divider class="mt-2 mb-3" />

        <v-row>
            <v-col style="text-align:right;">
                <v-btn small class="px-2 ml-2" color="bal-red" dark style="min-width: 0;" @click.stop="addNewRow">
                    <v-icon>add</v-icon>
                </v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mapState } from "vuex";
import Store from "@/store";

// Simple debounce function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export default {
    name: "rows-edit-tab",
    data() {
        return {
            isLoading: false,
            autocomplete: [],
            // Local values for each row to prevent lag
            localRowValues: []
        };
    },
    created() {
        // Create debounced version of updateRowField and calculateNewValues
        this.debouncedUpdateRow = debounce((value, field, index) => {
            value = !value ? null : value.replace(",", ".");
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceRowProperty", {
                prop: field,
                value: parseFloat(value),
                index: index
            });
            this.calculateNewValues(index);
        }, 300);
    },
    async mounted() {
        document.addEventListener("keydown", this.handleKeyboardListener);
        if (this.$route.params.exportInvoiceId !== "new") {
            this.isLoading = true;
        }

        // Initialize local values
        this.initializeLocalValues();
    },
    computed: {
        ...mapState(["products"]),
        ...mapState("ExportSaleInvoiceStore", ["activeExportInvoice"])
    },
    watch: {
        activeExportInvoice: async function() {
            if (
                this.activeExportInvoice.rows &&
                this.isLoading &&
                this.products.length > 0 &&
                this.activeExportInvoice.rows.length !== this.autocomplete.length
            ) {
                await this.activeExportInvoice.rows.forEach((row, index) => {
                    let product = this.products.find(p => p.name === row.product_name);
                    this.autocomplete.push(product);
                    this.updateComboboxValue(index);
                });
                this.isLoading = false;
            }
            // Sync local values when rows change
            this.initializeLocalValues();
        },
        'activeExportInvoice.rows': {
            handler: function(newRows) {
                // Keep local values in sync with store
                if (newRows && newRows.length > 0) {
                    newRows.forEach((row, index) => {
                        if (!this.localRowValues[index]) {
                            this.$set(this.localRowValues, index, {
                                sale_price: row.sale_price,
                                amount: row.amount,
                                own_price: row.own_price
                            });
                        }
                    });
                }
            },
            deep: true
        }
    },
    methods: {
        initializeLocalValues() {
            if (this.activeExportInvoice && this.activeExportInvoice.rows) {
                this.localRowValues = this.activeExportInvoice.rows.map(row => ({
                    sale_price: row.sale_price,
                    amount: row.amount,
                    own_price: row.own_price
                }));
            }
        },
        updateRowField(value, field, index) {
            value = !value ? null : value.replace(",", ".");
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceRowProperty", {
                prop: field,
                value: parseFloat(value),
                index: index
            });
            this.calculateNewValues(index);
        },
        addNewRow() {
            Store.commit("ExportSaleInvoiceStore/addNewActiveExportInvoiceRow");
            this.autocomplete.push(null);
            // Add empty local values for new row
            this.localRowValues.push({
                sale_price: null,
                amount: null,
                own_price: null
            });
        },
        deleteRow(index) {
            if (confirm("Are you sure you want to delete this row?")) {
                Store.commit("ExportSaleInvoiceStore/deleteActiveExportInvoiceRow", index);
                this.autocomplete.splice(index, 1);
                // Remove local values
                this.localRowValues.splice(index, 1);
            }
        },
        handleDuplicateClick(index) {
            Store.commit("ExportSaleInvoiceStore/addCopyActiveExportInvoiceRow", this.activeExportInvoice.rows[index]);
            if (this.activeExportInvoice.rows[index].product_name) {
                let product = this.products.find(p => p.name === this.activeExportInvoice.rows[index].product_name);
                this.autocomplete.push(product);
            } else {
                this.autocomplete.push(null);
            }
            // Duplicate local values
            const row = this.activeExportInvoice.rows[index];
            this.localRowValues.push({
                sale_price: row.sale_price,
                amount: row.amount,
                own_price: row.own_price
            });
        },
        async calculateNewValues(index) {
            let total_price = 0;
            let total_profit = 0;
            this.activeExportInvoice.rows.forEach(r => {
                if (r.sale_price && r.amount) {
                    r.sum = parseFloat(r.sale_price) * parseFloat(r.amount);
                    total_price += r.sum;
                } else {
                    r.sum = 0;
                }

                if (r.own_price && r.amount) {
                    r.profit = parseFloat(r.own_price) * parseFloat(r.amount);
                    if (!r.sale_price && r.product_type === "settings") {
                        r.profit = r.profit * -1;
                        total_profit += r.profit;
                    } else {
                        total_profit += r.sum - r.profit;
                    }
                } else {
                    r.profit = 0;
                }
            });
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: "total_net",
                value: total_price,
                index: index
            });
            Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceProperty", {
                prop: "total_profit",
                value: total_profit,
                index: index
            });
        },
        onlyIntegers($event) {
            let keyCode = $event.keyCode ? $event.keyCode : $event.which;
            if (keyCode < 44 || keyCode === 47 || keyCode === 45 || keyCode > 57) {
                $event.preventDefault();
            }
        },
        updateComboboxValue(index) {
            let x = this.autocomplete[index];
            if (x && x.id) {
                Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceRowProperty", {
                    prop: "product_name",
                    value: x.name,
                    index: index
                });
                if (x.export_price) {
                    // To update export_price(own_price) column
                    Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceRowProperty", {
                        prop: "own_price",
                        value: x.export_price,
                        index: index
                    });
                }
                if (x.code) {
                    // To update export_price(own_price) column
                    Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceRowProperty", {
                        prop: "code",
                        value: x.code,
                        index: index
                    });
                }
            } else {
                Store.commit("ExportSaleInvoiceStore/updateActiveExportInvoiceRowProperty", {
                    prop: "product_name",
                    value: x,
                    index: index
                });
            }
        }
    }
};
</script>

<style scoped>
.row-title {
    color: grey;
}

::v-deep .t-spacing > .v-input__control > .v-input__slot {
    min-height: 9px !important;
}

::v-deep .t-spacing .v-input__icon {
    padding-bottom: 8px;
}

::v-deep .v-text-field--outlined.v-input--dense .v-label {
    top: 4px;
    font-size: 13px;
}
::v-deep .v-text-field--outlined.v-input--dense .v-label.v-label--active {
    top: 8px;
}
</style>
